import { createContext, useContext, useState } from 'react'

import { AddInstrumentDialog } from '@/pages/instruments/market-summary/components/dialogs/AddInstrumentDialog'
import { AddInstrumentToDisplayDialog } from '@/pages/instruments/market-summary/components/dialogs/AddInstrumentToDisplayDialog'
import { AddMetricDialog } from '@/pages/instruments/market-summary/components/dialogs/AddMetricDialog'
import { CreateEventDialog } from '@/pages/instruments/market-summary/components/dialogs/CreateEventDialog'
import { RemoveEventDialog } from '@/pages/instruments/market-summary/components/dialogs/RemoveEventDialog'
import { RemoveInstrumentDialog } from '@/pages/instruments/market-summary/components/dialogs/RemoveInstrumentDialog'
import { RemoveInstrumentFromDisplayDialog } from '@/pages/instruments/market-summary/components/dialogs/RemoveInstrumentFromDisplayDialog'
import { RemoveMetricDialog } from '@/pages/instruments/market-summary/components/dialogs/RemoveMetricDialog'
import { UpdateInstrumentDialog } from '@/pages/instruments/market-summary/components/dialogs/UpdateInstrumentDialog'

type MarketSummaryAction =
  | {
      type: 'add-instrument'
    }
  | {
      type: 'remove-instrument'
      params: React.ComponentProps<typeof RemoveInstrumentDialog>['mutationParams']
    }
  | {
      type: 'update-instrument'
      params: React.ComponentProps<typeof UpdateInstrumentDialog>['mutationParams']
    }
  | {
      type: 'create-event'
    }
  | {
      type: 'remove-event'
      params: React.ComponentProps<typeof RemoveEventDialog>['mutationParams']
    }
  | {
      type: 'add-metric'
      params: React.ComponentProps<typeof AddMetricDialog>['mutationParams']
    }
  | {
      type: 'remove-metric'
      params: React.ComponentProps<typeof RemoveMetricDialog>['mutationParams']
    }
  | {
      type: 'add-instrument-to-display'
      params: React.ComponentProps<typeof AddInstrumentToDisplayDialog>['mutationParams']
    }
  | {
      type: 'remove-instrument-from-display'
      params: React.ComponentProps<typeof RemoveInstrumentFromDisplayDialog>['mutationParams']
    }

type GetActionParams<TType extends MarketSummaryAction['type']> =
  Extract<MarketSummaryAction, { type: TType }> extends { params: infer TParams } ? TParams : never

const useCreateMarketSummaryActionsContext = () => {
  const [actionState, setActionState] = useState<MarketSummaryAction | null>(null)

  const actions = {
    updateInstrument: (params: GetActionParams<'update-instrument'>) => {
      setActionState({ type: 'update-instrument', params })
    },
    removeEvent: (params: GetActionParams<'remove-event'>) => {
      setActionState({ type: 'remove-event', params })
    },
    removeInstrument: (params: GetActionParams<'remove-instrument'>) => {
      setActionState({ type: 'remove-instrument', params })
    },
    removeMetric: (params: GetActionParams<'remove-metric'>) => {
      setActionState({ type: 'remove-metric', params })
    },
    addInstrumentToDisplay: (params: GetActionParams<'add-instrument-to-display'>) => {
      setActionState({ type: 'add-instrument-to-display', params })
    },
    removeInstrumentFromDisplay: (params: GetActionParams<'remove-instrument-from-display'>) => {
      setActionState({ type: 'remove-instrument-from-display', params })
    },
    createEvent: () => {
      setActionState({ type: 'create-event' })
    },
    addInstrument: () => {
      setActionState({ type: 'add-instrument' })
    },
    addMetric: (params: GetActionParams<'add-metric'>) => {
      setActionState({ type: 'add-metric', params })
    },
    close: () => setActionState(null),
  }

  return {
    actionState,
    ...actions,
  }
}

const marketSummaryActionsContext = createContext<ReturnType<typeof useCreateMarketSummaryActionsContext> | null>(null)

export const useMarketSummaryActions = () => {
  const context = useContext(marketSummaryActionsContext)
  if (!context) {
    throw new Error('MarketSummaryActionsContext must be used within a <MarketSummaryActionsProvider/>.')
  }
  return context
}

export const MarketSummaryActionsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const context = useCreateMarketSummaryActionsContext()
  const state = context.actionState

  const dialogProps = (type: MarketSummaryAction['type']) => ({
    open: state?.type === type,
    onClose: context.close,
  })

  return (
    <marketSummaryActionsContext.Provider value={context}>
      {children}
      {state?.type === 'update-instrument' && (
        <UpdateInstrumentDialog
          key={state.params.instrument_id}
          mutationParams={state.params}
          {...dialogProps('update-instrument')}
        />
      )}
      {state?.type === 'remove-event' && (
        <RemoveEventDialog mutationParams={state.params} {...dialogProps('remove-event')} />
      )}
      {state?.type === 'remove-instrument' && (
        <RemoveInstrumentDialog mutationParams={state.params} {...dialogProps('remove-instrument')} />
      )}
      {state?.type === 'remove-metric' && (
        <RemoveMetricDialog mutationParams={state.params} {...dialogProps('remove-metric')} />
      )}
      {state?.type === 'add-instrument-to-display' && (
        <AddInstrumentToDisplayDialog mutationParams={state.params} {...dialogProps('add-instrument-to-display')} />
      )}
      {state?.type === 'remove-instrument-from-display' && (
        <RemoveInstrumentFromDisplayDialog
          mutationParams={state.params}
          {...dialogProps('remove-instrument-from-display')}
        />
      )}
      {state?.type === 'create-event' && <CreateEventDialog {...dialogProps('create-event')} />}
      {state?.type === 'add-instrument' && <AddInstrumentDialog {...dialogProps('add-instrument')} />}
      {state?.type === 'add-metric' && (
        <AddMetricDialog mutationParams={state?.params} {...dialogProps('add-metric')} />
      )}
    </marketSummaryActionsContext.Provider>
  )
}
